/**
 * BlockAuthoringPaletteIcons — Phase K.1.
 *
 * 18 SVG icons rendered in the floating Block Authoring Palette
 * (`BlockAuthoringPaletteShell`):
 *
 *   - 10 parameter icons: point / linear / polar / xy / rotation /
 *                         alignment / flip / visibility / lookup / basepoint
 *   - 8  action icons   : move / scale / stretch / polar-stretch /
 *                         rotation / flip / array / lookup
 *
 * Icons are intentionally minimal line-art (16x16 viewBox, currentColor
 * stroke) so they recolor with the theme. AutoCAD's own palette icons
 * are stylized 16x16 PNGs; we render simpler SVG strokes that read
 * cleanly at small sizes and scale crisply on hi-DPI displays.
 *
 * Phase K.2+K.3 will use the same `kind` discriminator to pair the
 * icon with the placement state machine for each parameter / action.
 */

import { type ReactElement } from "react";

export type ParameterIconKind =
  | "point"
  | "linear"
  | "polar"
  | "xy"
  | "rotation"
  | "alignment"
  | "flip"
  | "visibility"
  | "lookup"
  | "basepoint";

export type ActionIconKind =
  | "move"
  | "scale"
  | "stretch"
  | "polar-stretch"
  | "rotation"
  | "flip"
  | "array"
  | "lookup";

export type IconKind =
  | { kind: "parameter"; id: ParameterIconKind }
  | { kind: "action"; id: ActionIconKind };

export interface BlockAuthoringIconProps {
  /** Discriminated icon descriptor. */
  icon: IconKind;
  /** Optional className passed through to the root <svg>. */
  className?: string;
  /** Optional aria-label; defaults to "" so titles come from sibling label nodes. */
  ariaLabel?: string;
  /** Pixel size; default 16. */
  size?: number;
}

/**
 * Single entry-point used by the palette items. Routes to the per-kind
 * icon body and applies the shared svg attributes (viewBox/stroke/fill).
 */
export function BlockAuthoringIcon(props: BlockAuthoringIconProps): ReactElement {
  const size = props.size ?? 16;
  const ariaLabel = props.ariaLabel ?? "";
  const body =
    props.icon.kind === "parameter"
      ? renderParameterIcon(props.icon.id)
      : renderActionIcon(props.icon.id);
  return (
    <svg
      className={props.className}
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.4}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={ariaLabel ? "img" : undefined}
      aria-label={ariaLabel || undefined}
      aria-hidden={ariaLabel ? undefined : true}
      data-icon-kind={props.icon.kind}
      data-icon-id={props.icon.id}
    >
      {body}
    </svg>
  );
}

function renderParameterIcon(id: ParameterIconKind): ReactElement {
  switch (id) {
    case "point":
      // Single circle with a small dot — AutoCAD's "point parameter"
      // glyph is a hollow ring; we add the centre dot for clarity.
      return (
        <>
          <circle cx="8" cy="8" r="4.5" />
          <circle cx="8" cy="8" r="1" fill="currentColor" stroke="none" />
        </>
      );
    case "linear":
      // Horizontal arrow with end-caps — distance along an axis.
      return (
        <>
          <line x1="2" y1="8" x2="14" y2="8" />
          <line x1="2" y1="5" x2="2" y2="11" />
          <line x1="14" y1="5" x2="14" y2="11" />
          <polyline points="4,6 2,8 4,10" />
          <polyline points="12,6 14,8 12,10" />
        </>
      );
    case "polar":
      // Diagonal arrow + arc segment from base = distance + angle.
      return (
        <>
          <line x1="3" y1="13" x2="13" y2="3" />
          <polyline points="13,7 13,3 9,3" />
          <path d="M 3 13 A 6 6 0 0 1 9 7" />
        </>
      );
    case "xy":
      // Plus sign with arrowheads on both axes.
      return (
        <>
          <line x1="8" y1="2" x2="8" y2="14" />
          <line x1="2" y1="8" x2="14" y2="8" />
          <polyline points="6,4 8,2 10,4" />
          <polyline points="12,6 14,8 12,10" />
        </>
      );
    case "rotation":
      // Curved arrow → 270° arc with end-cap.
      return (
        <>
          <path d="M 12 4 A 5 5 0 1 0 13 9" />
          <polyline points="11,7 13,9 14.5,7" />
        </>
      );
    case "alignment":
      // Perpendicular-T glyph: line + perpendicular tick.
      return (
        <>
          <line x1="2" y1="11" x2="14" y2="11" />
          <line x1="8" y1="11" x2="8" y2="3" />
          <line x1="6" y1="3" x2="10" y2="3" />
        </>
      );
    case "flip":
      // Vertical mirror line + two opposing arrows.
      return (
        <>
          <line x1="8" y1="2" x2="8" y2="14" strokeDasharray="2 1" />
          <polyline points="3,5 6,8 3,11" />
          <polyline points="13,5 10,8 13,11" />
        </>
      );
    case "visibility":
      // Eye glyph: outer almond + inner pupil.
      return (
        <>
          <path d="M 2 8 Q 8 3 14 8 Q 8 13 2 8 Z" />
          <circle cx="8" cy="8" r="1.6" fill="currentColor" stroke="none" />
        </>
      );
    case "lookup":
      // Magnifier: circle + handle.
      return (
        <>
          <circle cx="7" cy="7" r="4" />
          <line x1="10" y1="10" x2="14" y2="14" />
        </>
      );
    case "basepoint":
      // Crosshair + bullseye = the insertion-base glyph.
      return (
        <>
          <circle cx="8" cy="8" r="5" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="8" y1="10" x2="8" y2="14" />
          <line x1="2" y1="8" x2="6" y2="8" />
          <line x1="10" y1="8" x2="14" y2="8" />
          <circle cx="8" cy="8" r="1.2" fill="currentColor" stroke="none" />
        </>
      );
  }
}

function renderActionIcon(id: ActionIconKind): ReactElement {
  switch (id) {
    case "move":
      // Box translated by an arrow vector.
      return (
        <>
          <rect x="2" y="2" width="6" height="6" />
          <line x1="6" y1="6" x2="13" y2="13" />
          <polyline points="10,13 13,13 13,10" />
        </>
      );
    case "scale":
      // Diagonal resize handles.
      return (
        <>
          <rect x="3" y="3" width="6" height="6" />
          <line x1="9" y1="9" x2="14" y2="14" />
          <polyline points="11,14 14,14 14,11" />
          <polyline points="3,6 3,3 6,3" />
        </>
      );
    case "stretch":
      // Hash-edge fence + horizontal arrow.
      return (
        <>
          <line x1="3" y1="3" x2="3" y2="13" strokeDasharray="2 1" />
          <line x1="13" y1="3" x2="13" y2="13" strokeDasharray="2 1" />
          <line x1="3" y1="8" x2="13" y2="8" />
          <polyline points="11,6 13,8 11,10" />
        </>
      );
    case "polar-stretch":
      // Polar arrow + stretch arc.
      return (
        <>
          <line x1="3" y1="13" x2="11" y2="5" />
          <polyline points="11,9 11,5 7,5" />
          <path d="M 3 13 A 8 8 0 0 1 13 13" strokeDasharray="2 1" />
        </>
      );
    case "rotation":
      // Same shape as parameter rotation but with an additional dot
      // (action vs parameter signal).
      return (
        <>
          <path d="M 12 4 A 5 5 0 1 0 13 9" />
          <polyline points="11,7 13,9 14.5,7" />
          <circle cx="3" cy="13" r="1.3" fill="currentColor" stroke="none" />
        </>
      );
    case "flip":
      // Mirror with object L-R reflection.
      return (
        <>
          <line x1="8" y1="2" x2="8" y2="14" strokeDasharray="2 1" />
          <path d="M 2 5 L 5 5 L 5 11 L 2 11 Z" />
          <path d="M 14 5 L 11 5 L 11 11 L 14 11 Z" />
        </>
      );
    case "array":
      // 3x3 grid of small squares.
      return (
        <>
          <rect x="2" y="2" width="3" height="3" />
          <rect x="6.5" y="2" width="3" height="3" />
          <rect x="11" y="2" width="3" height="3" />
          <rect x="2" y="6.5" width="3" height="3" />
          <rect x="6.5" y="6.5" width="3" height="3" />
          <rect x="11" y="6.5" width="3" height="3" />
          <rect x="2" y="11" width="3" height="3" />
          <rect x="6.5" y="11" width="3" height="3" />
          <rect x="11" y="11" width="3" height="3" />
        </>
      );
    case "lookup":
      // Same magnifier as parameter lookup, but with a small "+" inside
      // the circle to suggest an active action.
      return (
        <>
          <circle cx="7" cy="7" r="4" />
          <line x1="10" y1="10" x2="14" y2="14" />
          <line x1="5.5" y1="7" x2="8.5" y2="7" />
          <line x1="7" y1="5.5" x2="7" y2="8.5" />
        </>
      );
  }
}
